import type { DateTime } from 'luxon';
import type { VehiclePosition } from '..';
import type { IJourneyCall } from '../models/journey-call.model';
import type { IJourneyPrognosis } from '../models/journey-prognosis.model';

export class JourneyPanelHelper {
  public static isVehicleAtCall(call: IJourneyCall, vehicle?: VehiclePosition, now = Date.now()): boolean {
    if (call.passed || !vehicle || vehicle.atStop !== true || !call.stopPointGid || call.stopPointGid !== vehicle.lastStopPointGid) return false;
    const age = now - Date.parse(vehicle.timestamp);
    return age >= 0 && age < 60000;
  }

  public static getRemainingCalls(calls: IJourneyCall[], train: boolean): IJourneyCall[] {
    const sourceCalls = calls.filter((call) => (train ? !!call.locationSignature : !!call.stopPointGid)).sort((a, b) => a.order - b.order);
    const lastPassedOrder = train ? sourceCalls.reduce<number | undefined>((order, call) => call.passed ? call.order : order, undefined) : undefined;
    return sourceCalls.filter((call) =>
      !(call.cancelled && !call.stopAreaName?.trim()) &&
      (lastPassedOrder === undefined || call.order > lastPassedOrder) &&
      !call.passed,
    );
  }

  public static timetableOnly(prognosis: IJourneyPrognosis): IJourneyPrognosis {
    return {
      ...prognosis,
      cancelled: false,
      delayInMinutes: null,
      calls: prognosis.calls.map((call) => ({ ...call, ...JourneyPanelHelper.withoutReportedTimes, cancelled: false })),
    };
  }

  /**
   * NV-9135: progress is taken from the vehicle's last reported stop for every SIRI-ET journey, since the producer
   * advances journeys without real-time data by the clock and often marks calls of real-time journeys as passed before
   * the vehicle reaches them. A stop visited twice on the journey is resolved to the visit closest to where the
   * producer places the vehicle. Only scheduled times are kept for a timetable only journey.
   */
  public static withPositionProgress(calls: IJourneyCall[], vehicle: Pick<VehiclePosition, 'lastStopPointGid' | 'atStop'> | undefined, timetableOnly: boolean): IJourneyCall[] {
    const reportedTimes = timetableOnly ? JourneyPanelHelper.withoutReportedTimes : {};
    // Without any reported stop, the producer's progression is the best estimate there is.
    if (!vehicle?.lastStopPointGid) return timetableOnly ? calls.map((call) => ({ ...call, ...reportedTimes })) : calls;
    const scheduledOrder = calls.find((call) => !call.passed)?.order ?? Number.MAX_SAFE_INTEGER;
    const lastStop = calls
      .filter((call) => call.stopPointGid === vehicle.lastStopPointGid)
      .reduce<IJourneyCall | undefined>((closest, call) => (!closest || Math.abs(call.order - scheduledOrder) < Math.abs(closest.order - scheduledOrder) ? call : closest), undefined);
    // A real-time journey keeps the producer's progression when the reported stop is not on the journey.
    if (!lastStop && !timetableOnly) return calls;
    return calls.map((call) => ({
      ...call,
      ...reportedTimes,
      passed: !!lastStop && (call.order < lastStop.order || (call.order === lastStop.order && vehicle.atStop !== true)),
    }));
  }

  public static hasPreviousCalls(calls: IJourneyCall[], remaining: IJourneyCall[], train: boolean): boolean {
    const first = remaining[0];
    return !!first && calls.some((call) => call.passed && call.order < first.order && (train ? !!call.locationSignature : !!call.stopPointGid));
  }

  public static hasArrivalTimes(calls: IJourneyCall[], live: boolean): boolean {
    return calls.some((call) => {
      const times = JourneyPanelHelper.getCallTimes(call);
      return !!times.planned || (live && !!(times.actual ?? times.estimated));
    });
  }

  public static getTransitionCalls(previous: IJourneyCall[], remaining: IJourneyCall[]): IJourneyCall[] {
    const first = remaining[0];
    if (!first) return remaining;
    const leaving = previous.filter((call) => call.order < first.order && !remaining.some((next) => next.order === call.order));
    return [...leaving, ...remaining];
  }

  /**
   * NV-9111: the first call of a SIRI-ET journey has no arrival, only a departure, so its departure times are
   * used in place of the arrival times whenever a call has no arrival time at all.
   */
  public static getCallTimes(call: IJourneyCall): { planned: DateTime | null; actual: DateTime | null; estimated: DateTime | null } {
    return call.plannedArrivalTime || call.actualArrivalTime || call.estimatedArrivalTime
      ? { planned: call.plannedArrivalTime, actual: call.actualArrivalTime, estimated: call.estimatedArrivalTime }
      : { planned: call.plannedDepartureTime, actual: call.actualDepartureTime, estimated: call.estimatedDepartureTime };
  }

  public static getArrival(call: IJourneyCall, requireRealtime = false): { time: DateTime | null; planned: DateTime | null; kind: 'actual' | 'estimated' | 'planned' } {
    const times = JourneyPanelHelper.getCallTimes(call);
    const reported = times.actual ?? times.estimated;
    const kind = times.actual ? 'actual' : times.estimated ? 'estimated' : 'planned';
    return {
      time: reported ?? (requireRealtime ? null : times.planned),
      planned: reported && times.planned && !reported.hasSame(times.planned, 'minute') ? times.planned : null,
      kind,
    };
  }

  private static readonly withoutReportedTimes = { actualArrivalTime: null, estimatedArrivalTime: null, actualDepartureTime: null, estimatedDepartureTime: null };

}
